import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import Switch from "react-switch";
import "./rules.css";
import { fetchThemeData, selectTheme } from "../../themeSlice";
import { processQuestions } from "../../questionSlice";
import { selectAdminToken } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import ColorPallet from "../color_pallet/colorPallet";
import { updateThemeData } from "../../functions/updateThemeData";


const Rules = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const [isIntroModelOpen, setisIntroModelOpen] = useState(false);
  // bring in all four values from your theme slice exactly as before
  const { availableThemes, currentTheme, status, data } = useSelector(
    (state) => state.theme
  );

  // left‐side: rules
  const [rules, setRules] = useState([""]);

  // right‐side: page metadata
  const [mainTitle, setMainTitle] = useState("");
  const [thankYouText, setThankYouText] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scorePerQuestion, setScorePerQuestion] = useState(1);
  const [shuffleEnable, setshuffleEnable] = useState(false);

  const [colors, setColors] = useState({
    text_color: "#000000",
    ui_color_1: "#222222",
    ui_color_2: "#222222",
    option_color: "#ff9900",
    option_text_color: "#ffffff",
  });

  useEffect(() => {
    if (!data?.colors) return;
    setColors(data.colors);
  }, [data]);

  const handleColorChange = (key) => (e) => {
    setColors((prev) => ({ ...prev, [key]: e.target.value }));
  };

  // fetch questions & theme on mount
  useEffect(() => {
    dispatch(processQuestions());
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  // when theme data arrives, populate both rules + metadata
  useEffect(() => {
    if (!data) return;
    setRules(data.rules || [""]);
    setMainTitle(data.main_title || "");
    setThankYouText(data.custom_text_thank_you_page || "");
    setScorePerQuestion(data.correctPoints || 1);
    setshuffleEnable(Boolean(data.shuffle));
  }, [data]);

  // --- rules handlers ---
  const handleRuleChange = (idx, val) => {
    let newVal = val;
    if (val.length > 100) {
      Swal.fire(
        "Limit Exceeded",
        "Rules can be at most 100 characters. extra text has been trimmed.",
        "warning"
      );
      newVal = val.slice(0, 100);
    }
    const arr = [...rules];
    arr[idx] = newVal;
    setRules(arr);
  };

  const handleAddRule = () => {
    if (rules.length >= 6) {
      return Swal.fire(
        "Limit Reached",
        "You can only add up to 6 rules.",
        "warning"
      );
    }
    setRules([...rules, ""]);
  };
  const handleRemoveRule = (idx) => {
    if (rules.length <= 1) {
      return Swal.fire(
        "At Least One Rule Required",
        "You must have at least one rule.",
        "warning"
      );
    }
    setRules(rules.filter((_, i) => i !== idx));
  };

  const handleMainTitleChange = (val) => {
    let newVal = val;
    if (val.length > 150) {
      Swal.fire(
        "Limit Exceeded",
        "Main Title can be at most 150 characters.",
        "warning"
      );
      newVal = val.slice(0, 150);
    }
    setMainTitle(newVal);
  };

  const handleThankYouTextChange = (val) => {
    let newVal = val;
    if (val.length > 300) {
      Swal.fire(
        "Limit Exceeded",
        "Thank You Text can be at most 300 characters.",
        "warning"
      );
      newVal = val.slice(0, 300);
    }
    setThankYouText(newVal);
  };

  const handleScorePerQuestionChange = (val) => {
    if (val === "") {
      setScorePerQuestion("");
      return;
    }

    const parsedVal = parseInt(val, 10);
    if (Number.isNaN(parsedVal)) return;
    setScorePerQuestion(Math.max(1, parsedVal));
  };

  // --- submit all ---
  const handleSubmit = async () => {
    const hasEmptyRule = rules.some((rule) => rule.trim() === "");
    if (hasEmptyRule) {
      Swal.fire(
        "Rule Required",
        "Please fill in all rule inputs before saving.",
        "warning"
      );
      return;
    }

    if (String(scorePerQuestion).trim() === "") {
      Swal.fire(
        "Score Required",
        "Score Per Question cannot be empty.",
        "warning"
      );
      return;
    }

    const numericScore = parseInt(scorePerQuestion, 10);
    if (Number.isNaN(numericScore) || numericScore < 1) {
      Swal.fire(
        "Invalid Score",
        "Score Per Question must be at least 1.",
        "warning"
      );
      return;
    }

    const payload = {
      data: {
        rules,
        main_title: mainTitle,
        custom_text_thank_you_page: thankYouText,
        colors,
        correctPoints: numericScore,
        shuffle: shuffleEnable,
      },
      currentTheme,
    };

    try {
      await updateThemeData({ payload, token: adminToken });
      // Swal.fire("Success", "Settings saved successfully!", "success");
      setTimeout(() => navigate("/admin/questions"), 500);
    } catch (e) {
      Swal.fire(
        "Error",
        e.message || "Failed to save. Please try again.",
        "error"
      );
    }
  };

  return (
    <>

      <div className="rules-page-layout">
        <div className="back-button-holder">
          <button
            className="back-button"
            onClick={() => navigate("/admin/")}
          >
            <i className="fa-solid fa-arrow-left"></i> Back
          </button>
        </div>
        <div className="rules-page-new">
        <div className="admin-tabs-holder">
          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-pen"></i> &nbsp; Basic Theme Details
            </div>
            <div className="form-group-v1">
              <label>Main Title:</label>
              <input
                type="text"
                value={mainTitle}
                onChange={(e) => handleMainTitleChange(e.target.value)}
                placeholder="Main title for rules"
              />
              <div className="code">
                * Maximum character limit should be 150 characters.
              </div>
            </div>
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-trophy"></i> &nbsp; Game Rules
            </div>
            <h3 className="custom-rules-title">Custom rules</h3>
            {rules.map((rule, i) => (
              <div className="rule-row" key={i}>
                <span className="rule-number">{i + 1}.</span>
                <input
                  className="rule-input"
                  type="text"
                  placeholder="Enter rule"
                  value={rule}
                  onChange={(e) => handleRuleChange(i, e.target.value)}
                />
                <button
                  className="remove-btn"
                  onClick={() => handleRemoveRule(i)}
                >
                  &times;
                </button>
              </div>
            ))}
            <div className="rules-button-container">
              <div>
                <button className="add-btn" onClick={handleAddRule}>
                  + Add New Rule
                </button>
              </div>
              <div className="code">
                * Keep 6 rules at the most. <br></br>* At least 1 rule is
                required.<br></br>* character limit of each rule is 100<br></br>
              </div>
            </div>
          </div>

          <div className="admin-tab-container">
            <div className="admin-tab-title">
              <i className="fa-solid fa-palette"></i> &nbsp; Color Customization
            </div>
            <div className="pallet-holder">
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.text_color}
                      onChange={handleColorChange("text_color")}
                    />
                    <label>Text Color</label>
                  </div>
                  <div className="color-code">{colors.text_color}</div>
                </div>
              </div>
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.option_color}
                      onChange={handleColorChange("option_color")}
                    />
                    <label>button color</label>
                  </div>
                  <div className="color-code">{colors.option_color}</div>
                </div>
              </div>
              <div className="pallet-row">
                <div className="pallet-input">
                  <div className="color-holder">
                    <input
                      type="color"
                      className="pallet-swatch"
                      value={colors.option_text_color}
                      onChange={handleColorChange("option_text_color")}
                    />
                    <label>button text color</label>
                  </div>
                  <div className="color-code">{colors.option_text_color}</div>
                </div>
              </div>
              <div className="form-group-v1 per-question-container">
                <label>Score Per Question</label>
                <input
                  type="number"
                  min="1"
                  value={scorePerQuestion}
                  onChange={(e) => handleScorePerQuestionChange(e.target.value)}
                />
              </div>
              <div className="toggle-group">
                <div className="toggle-item">
                  <label>Shuffle Question</label>
                  <Switch
                    checked={shuffleEnable}
                    onChange={setshuffleEnable}
                    onColor="#4caf50"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="rules-action-holder">
            <button className="save-and-continue" onClick={handleSubmit}>
              Save & Continue
            </button>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default Rules;
